import { useEffect, useMemo, useRef } from "react";
import { NavLink, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Users,
  UsersRound,
  Calendar,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  Store,
  ShoppingBag,
  ClipboardList,
  MessageCircle,
  CircleHelp,
  BookOpen,
  Wrench,
} from "lucide-react";
import { cn, Button, toast } from "@cliently/ui";
import { useUiStore } from "../../stores/ui.store";
import { usePermissions } from "../../hooks/use-permissions";
import { useAuthStore } from "../../stores/auth.store";
import { useConversations } from "../../hooks/useChat";
import { isBusinessPlanForTeam } from "../../lib/team-plan";
import { BrandMark } from "./BrandMark";



export const Sidebar = () => {
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUiStore();
  const { t } = useTranslation();
  const location = useLocation();
  const { isAdmin } = usePermissions();
  const org = useAuthStore((s) => s.org);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: conversations = [] } = useConversations();
  const plan = org?.plan || "FREE";
  const knownMessageIdsRef = useRef<Set<string>>(new Set());

  const unreadChatCount = useMemo(() => {
    if (location.pathname.startsWith("/chat")) return 0;
    return conversations.filter((conversation) => {
      if (typeof conversation.hasUnread === "boolean") {
        return conversation.hasUnread;
      }
      const lastMessage = conversation.lastMessage;
      if (!lastMessage) return false;
      const iSentLast = Boolean(
        currentUserId &&
          lastMessage.senderId === currentUserId &&
          !lastMessage.senderClientId,
      );
      return !iSentLast;
    }).length;
  }, [conversations, location.pathname, currentUserId]);

  useEffect(() => {
    if (!conversations.length) return;

    const knownMessageIds = knownMessageIdsRef.current;
    if (knownMessageIds.size === 0) {
      conversations.forEach((conversation) => {
        if (conversation.lastMessage?.id) {
          knownMessageIds.add(conversation.lastMessage.id);
        }
      });
      return;
    }

    if (location.pathname.startsWith("/chat")) {
      conversations.forEach((conversation) => {
        if (conversation.lastMessage?.id) {
          knownMessageIds.add(conversation.lastMessage.id);
        }
      });
      return;
    }

    conversations.forEach((conversation) => {
      const lastMessage = conversation.lastMessage;
      if (!lastMessage) return;
      if (knownMessageIds.has(lastMessage.id)) return;

      knownMessageIds.add(lastMessage.id);
      if (lastMessage.senderId === currentUserId) return;

      toast.info(`Nouveau message de ${conversation.peerLabel ?? "Conversation"}`, {
        description: lastMessage.content,
      });
    });
  }, [conversations, currentUserId, location.pathname]);

  /** Groupée pour casser la liste plate et refléter les usages métier (plutôt qu'un menu technique uniforme). */
  const navGroups: Array<{
    label: string;
    items: Array<{
      to: string;
      label: string;
      icon: typeof LayoutDashboard;
      end?: boolean;
    }>;
  }> = [
    {
      label: t("nav.groupPilotage", "Pilotage"),
      items: [
        { to: "/", label: t("nav.dashboard"), icon: LayoutDashboard, end: true },
      ],
    },
    {
      label: t("nav.groupClients", "Clients & activité"),
      items: [
        { to: "/clients", label: t("nav.clients"), icon: Users },
        { to: "/calendar", label: t("nav.calendar"), icon: Calendar },
        { to: "/invoices", label: t("nav.invoices"), icon: FileText },
      ],
    },
    {
      label: t("nav.groupOffering", "Accompagnement"),
      items: [
        { to: "/programs", label: t("nav.programs", "Programmes"), icon: BookOpen },
        { to: "/services", label: t("nav.services"), icon: ShoppingBag },
        { to: "/forms", label: t("nav.forms"), icon: ClipboardList },
      ],
    },
    {
      label: t("nav.groupOrg", "Organisation"),
      items: [
        { to: "/chat", label: t("nav.chat", "Messages"), icon: MessageCircle },
        ...(isAdmin && isBusinessPlanForTeam(plan)
          ? [{ to: "/team", label: t("nav.team"), icon: UsersRound }]
          : []),
        { to: "/site", label: t("nav.site"), icon: Store },
        { to: "/tools", label: t("nav.tools", "Outils"), icon: Wrench },
        { to: "/settings", label: t("nav.settings"), icon: Settings, end: true },
      ],
    },
  ];

  const helpItem = {
    to: "/help",
    label: t("nav.help"),
    icon: CircleHelp,
  };

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label={t("nav.closeMenu", "Fermer le menu")}
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          "bg-sidebar fixed inset-y-0 left-0 z-50 flex flex-col border-r transition-all duration-300",
          sidebarOpen ? "w-64" : "w-16",
          "max-md:translate-x-0",
          !sidebarOpen && "max-md:-translate-x-full",
        )}
      >
        {/* Marque */}
        <div className="flex h-16 items-center border-b px-3">
          {sidebarOpen ? (
            <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2.5">
                <BrandMark />
                <span className="flex min-w-0 flex-col leading-none">
                  <span className="text-foreground truncate text-[15px] font-semibold tracking-tight">
                    Cliently
                  </span>
                  <span className="text-muted-foreground truncate text-[11px]">
                    {t("nav.brandTagline", "Espace pro")}
                  </span>
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className="hidden shrink-0 md:flex"
                aria-label={t("nav.collapseSidebar", "Réduire le menu")}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            // Repliée : le logo et le bouton n'ont pas assez de place côte à
            // côte (rail à 64px). On fusionne les deux en un seul élément :
            // le logo par défaut, une flèche au survol pour agrandir.
            <button
              type="button"
              onClick={toggleSidebar}
              className="group relative mx-auto hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-muted/60 md:flex"
              aria-label={t("nav.expandSidebar", "Agrandir le menu")}
            >
              <span className="transition-opacity group-hover:opacity-0">
                <BrandMark />
              </span>
              <ChevronRight className="text-sidebar-foreground/70 absolute h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="min-h-0 flex-1 space-y-4 overflow-y-auto p-2 pt-3">
          {navGroups.map((group) => (
            <div key={group.label} className="space-y-0.5">
              {sidebarOpen && (
                <p className="text-sidebar-foreground/45 px-3 pb-1 text-[10.5px] font-semibold uppercase tracking-wider">
                  {group.label}
                </p>
              )}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => {
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-sm font-medium transition-[color,background-color,border-color]",
                      isActive
                        ? "bg-primary/9 text-primary border-primary/25"
                        : "text-sidebar-foreground/70 hover:border-border/70 hover:bg-muted/45 hover:text-sidebar-foreground",
                      !sidebarOpen && "justify-center px-2",
                    )
                  }
                >
                  <span className="relative shrink-0">
                    <item.icon className="h-5 w-5 shrink-0" aria-hidden />
                    {item.to === "/chat" && unreadChatCount > 0 ? (
                      <span className="bg-destructive text-destructive-foreground absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
                        {unreadChatCount > 9 ? "9+" : unreadChatCount}
                      </span>
                    ) : null}
                  </span>
                  {sidebarOpen && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="flex justify-center p-2">
          <NavLink
            to={helpItem.to}
            onClick={() => {
              if (window.innerWidth < 768) setSidebarOpen(false);
            }}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-xs font-normal transition-[color,background-color,border-color]",
                isActive
                  ? "bg-primary/9 text-primary border-primary/25"
                  : "text-sidebar-foreground/60 hover:border-border/70 hover:bg-muted/45 hover:text-sidebar-foreground",
              )
            }
          >
            <helpItem.icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
            {sidebarOpen && <span>{helpItem.label}</span>}
          </NavLink>
        </div>

        {/* Footer */}
        <div className="border-t p-3">
          {sidebarOpen && (
            <p className="text-sidebar-foreground/35 truncate text-center text-[10.5px]">
              {t("nav.brandFooter", "Cliently — votre activité, simplifiée")}
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
